import { css } from '@compiled/react';
import React, { type FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useResizingHeight } from '@atlaskit/motion';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';
import { useConfluencePageAri } from '@atlassian/ai-summary';
import { AIBorder, AIStreamLoading, AISummaryLoading } from '@atlassian/ai-components';
import type { ConvoAIStreamingState } from '@atlassian/ai-agent-streaming';
import { ConvoAIResponseState, useConvoAIStreaming } from '@atlassian/ai-agent-streaming';
import type {
	AiFeatureRequestEnvelopeConfluenceQuickSummaryRequest,
	QuickSummaryFinalResponseMessage,
	QuickSummaryFollowUpQueriesResponseMessage,
	QuickSummaryErrorResponseMessage,
} from '@atlassian/convo-ai-api';
import { useAIMockStreaming } from '@atlassian/ai-mock-streaming';

import { useSessionData } from '@confluence/session-data';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
	QUICK_SUMMARY_EXPERIENCE,
	QUICK_SUMMARY_WHITEBOARDS_EXPERIENCE,
} from '@confluence/experience-tracker';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { useInlineLinkingStreaming } from '../hooks/useInlineLinkingStreaming';
import { mapQuickSummaryInlineLinkingAgentResponseToMarkdown } from '../hooks/mapQuickSummaryInlineLinkingAgentResponseToMarkdown';
import { useQuickSummaryAnalyticsProperties } from '../hooks/useQuickSummaryAnalyticsProperties';
import { buildSummaryRequest } from '../effects/summaryRequestBuilder';
import { SmartsSummaryErrorComponent } from '../shared-components/SmartsSummaryErrorComponent';
import { QuickSummaryComponent } from '../platform-ui/QuickSummaryComponent';
import { QUICK_SUMMARY_DIALOG_INTERACTION_METRIC } from '../perf.config';
import { MAX_SUMMARY_HEIGHT } from '../shared-components/SummaryPopupScrollableBody';
import { validateMarkdownByContentType } from '../effects/aiOutputValidation';

import type { QuickSummaryProps } from './QuickSummaryStreamingEntryPoint';

const styles = css({
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/design-system/use-tokens-typography
	h3: { fontSize: `${token('font.heading.small')} !important` },
});

export type SummaryLinkType = 'default' | 'inline' | 'first_party';

export const QuickSummaryStreamingWrapper: FC<QuickSummaryProps> = ({
	contentId,
	spaceKey,
	lastModifiedDate,
	source,
	contentPath,
	contentType,
	setIsOpen,
	isInObjectSidebar,
}) => {
	const summaryRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(summaryRef);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { locale } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isRovoEnabled, cloudId } = useSessionData();
	const { pageAri } = useConfluencePageAri(cloudId, contentId, contentType);
	const {
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIInteractionDismiss,
	} = useAIEventsInstrumentation();
	const isNonCachedResponse = useRef(false);
	const isStreamingDone = useRef(false);
	const currentAnswerChunk = useRef(0);

	const contentLastUpdated = Math.floor(new Date(lastModifiedDate).getTime() / 1000);
	const experienceTrackerName =
		contentType === 'whiteboard' ? QUICK_SUMMARY_WHITEBOARDS_EXPERIENCE : QUICK_SUMMARY_EXPERIENCE;

	const { analyticsProperties } = useQuickSummaryAnalyticsProperties(
		contentLastUpdated,
		contentType,
	);

	const {
		inlineLinkingContent,
		updateBuffer,
		lastProcessedLength,
		setLastProcessedLength,
		isBufferStreaming,
	} = useInlineLinkingStreaming(35, 5);

	const summaryLinkType = expVal(
		'confluence_ai_1p_inline_linking_quick_summary',
		'cohort',
		'default',
	) as SummaryLinkType;

	const setIsStreamingDone = () => {
		isStreamingDone.current = true;
	};

	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'quickSummary',
					source,
					actionSubjectId: 'quickSummaryDialog',
					attributes: {
						contentId,
						contentType,
						dwellTime: getDwellTime(),
						isAIMateSummary: true,
					},
				},
			}).fire();
			if (!isStreamingDone.current) {
				trackAIInteractionDismiss();
			}
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [request, config] = useMemo(
		() =>
			buildSummaryRequest({
				contentAri: pageAri || '',
				locale,
				enableFollowUp: isRovoEnabled,
				summaryLinkType,
			}),
		[isRovoEnabled, locale, pageAri, summaryLinkType],
	);

	const updateInlineLinkingContent = useCallback(
		(answerPartContent: string) => {
			const fullContent = mapQuickSummaryInlineLinkingAgentResponseToMarkdown(answerPartContent);
			const newContent = fullContent.slice(lastProcessedLength);
			updateBuffer(newContent);
			setLastProcessedLength(fullContent.length);
		},
		[updateBuffer, setLastProcessedLength, lastProcessedLength],
	);

	const onStateChange = useCallback(
		(
			state: ConvoAIStreamingState<
				QuickSummaryFinalResponseMessage,
				QuickSummaryErrorResponseMessage,
				QuickSummaryFollowUpQueriesResponseMessage
			>,
		) => {
			switch (state.responseState) {
				case ConvoAIResponseState.Initialization:
					if (state.content === null) {
						experienceTracker.start({
							name: experienceTrackerName,
							attributes: {
								contentId,
							},
						});
						QUICK_SUMMARY_DIALOG_INTERACTION_METRIC.start();
						trackAIInteractionInit();
					}
					break;
				case ConvoAIResponseState.Error:
					if (state.error) {
						const { status_code: aiErrorCode, message_template: aiErrorMessage } =
							state.error.message;

						QUICK_SUMMARY_DIALOG_INTERACTION_METRIC.stop();
						trackAIResultError({
							aiErrorCode,
							aiErrorMessage,
						});
						setIsStreamingDone();
						break;
					}
					break;
				case ConvoAIResponseState.AnswerPart:
					isNonCachedResponse.current = true;

					if (
						summaryLinkType === 'inline' &&
						currentAnswerChunk.current !== state.answerChunkIndex
					) {
						updateInlineLinkingContent(state.content);
					}

					currentAnswerChunk.current = state.answerChunkIndex;
					break;
				case ConvoAIResponseState.FinalResponse:
					if (state.content) {
						const summaryHeight = summaryRef.current?.scrollHeight ?? 0;
						createAnalyticsEvent({
							type: 'sendScreenEvent',
							data: {
								name: 'quickSummaryDialog',
								attributes: {
									contentId,
									contentType,
									summaryLocale: 'en-US',
									contentLastUpdated,
									summaryHeight,
									maxSummaryHeight: MAX_SUMMARY_HEIGHT,
									isAIMateSummary: true,
									isValidSummary: validateMarkdownByContentType(contentType, state.content),
								},
							},
						}).fire();
						trackAIResultView();
						QUICK_SUMMARY_DIALOG_INTERACTION_METRIC.stop();
						setIsStreamingDone();
					}
					break;
				default:
					break;
			}
		},
		[
			contentId,
			contentLastUpdated,
			contentType,
			createAnalyticsEvent,
			experienceTracker,
			experienceTrackerName,
			trackAIResultError,
			trackAIInteractionInit,
			trackAIResultView,
			summaryLinkType,
			updateInlineLinkingContent,
		],
	);

	const [{ content, responseState, error, followUpContent }] = useConvoAIStreaming<
		AiFeatureRequestEnvelopeConfluenceQuickSummaryRequest,
		QuickSummaryFinalResponseMessage,
		QuickSummaryErrorResponseMessage,
		QuickSummaryFollowUpQueriesResponseMessage
	>(request, config, onStateChange);

	const { delayedContent, isComplete } = useAIMockStreaming(content || '');

	const renderContent = () => {
		switch (responseState) {
			case ConvoAIResponseState.Initialization:
				return isInObjectSidebar ? (
					<Box paddingInline="space.100">
						<AISummaryLoading />
					</Box>
				) : (
					<AIStreamLoading />
				);
			case ConvoAIResponseState.Error:
				return (
					<SmartsSummaryErrorComponent
						analyticsProperties={analyticsProperties}
						errorFromApolloOrStream={error}
						contentId={contentId}
						experienceName={experienceTrackerName}
					/>
				);
			case ConvoAIResponseState.AnswerPart:
				if (summaryLinkType === 'inline') {
					return (
						<QuickSummaryComponent
							ref={summaryRef}
							analyticsProperties={analyticsProperties}
							content={inlineLinkingContent}
							contentId={contentId}
							contentPath={contentPath}
							contentType={contentType}
							hideFooter
							hideTelepointer={false}
							isRovoEnabled={isRovoEnabled}
							setIsOpen={setIsOpen}
							spaceKey={spaceKey}
							isInObjectSidebar={isInObjectSidebar}
						/>
					);
				}
			case ConvoAIResponseState.FinalResponse:
			case ConvoAIResponseState.FollowUp:
				const isExperienceSuccess =
					responseState === ConvoAIResponseState.FinalResponse ||
					responseState === ConvoAIResponseState.FollowUp;

				const isStreamingInProgress =
					fg('ai-smart-button-team-25') && (!isStreamingDone.current || !isComplete);

				const isResponseStateAnswerPart = responseState === ConvoAIResponseState.AnswerPart;
				let hideFooter = fg('ai-smart-button-team-25')
					? isStreamingInProgress
					: isResponseStateAnswerPart;

				if (!isBufferStreaming) {
					hideFooter = false;
				}

				let contentToRender = content || '';

				if (summaryLinkType === 'inline') {
					contentToRender = inlineLinkingContent;
				}

				if (
					responseState === ConvoAIResponseState.FinalResponse &&
					!isNonCachedResponse.current &&
					fg('ai-smart-button-team-25')
				) {
					contentToRender = delayedContent;
				}

				return (
					<>
						<QuickSummaryComponent
							ref={summaryRef}
							analyticsProperties={analyticsProperties}
							content={contentToRender}
							contentId={contentId}
							contentPath={contentPath}
							contentType={contentType}
							followUpContent={isStreamingInProgress ? undefined : followUpContent}
							hideFooter={hideFooter}
							hideTelepointer={!hideFooter}
							isRovoEnabled={isRovoEnabled}
							setIsOpen={setIsOpen}
							spaceKey={spaceKey}
							isInObjectSidebar={isInObjectSidebar}
						/>
						{isExperienceSuccess && <ExperienceSuccess name={QUICK_SUMMARY_EXPERIENCE} />}
					</>
				);
		}
	};

	const { ref } = useResizingHeight();

	if (isInObjectSidebar) {
		return (
			<div css={styles} ref={ref}>
				{renderContent()}
			</div>
		);
	}

	return (
		<AIBorder loading={responseState === ConvoAIResponseState.Initialization}>
			<div ref={ref}>{renderContent()}</div>
		</AIBorder>
	);
};
